import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, User, Mail, Phone, Shield, Wrench, X, Folder, Building2 } from 'lucide-react';
import Select from 'react-select';
import api from '../../services/api';
import { toast } from 'react-toastify';
import styles from './SettingsPage.module.css';
import commonStyles from '../../styles/common.module.css';

const UserList = () => {
  const [users, setUsers] = useState([]);
  const [formData, setFormData] = useState({
    username: '',
    first_name: '',
    last_name: '',
    email: '',
    telegram_chat_id: '',
    password: '',
    service_centers: [],
    roles: [],
    projects: [], // Добавлено
    guest_customers: [],
  });
  const [serviceCenters, setServiceCenters] = useState([]);
  const [roles, setRoles] = useState([]);
  const [projects, setProjects] = useState([]); // Добавлено
  const [customers, setCustomers] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const [serviceCentersResponse, rolesResponse, projectsResponse, customersResponse] = await Promise.all([
          api.get('/settings/service-centers/'),
          api.get('/settings/roles/'),
          api.get('/settings/projects/'), // Добавлено
          api.get('/settings/customers/'),
        ]);
        
        setServiceCenters(
          serviceCentersResponse.data.map(sc => ({
            value: sc.id,
            label: sc.name,
          }))
        );
        setRoles(
          rolesResponse.data.map(role => ({
            value: role.id,
            label: role.name,
          }))
        );
        setProjects(
          projectsResponse.data.map(project => ({
            value: project.id,
            label: project.name,
          }))
        ); // Добавлено
        setCustomers(
          customersResponse.data.map(customer => ({
            value: customer.id,
            label: customer.name,
          }))
        );

        const usersResponse = await api.get('/settings/users/');
        setUsers(usersResponse.data);
      } catch (error) {
        toast.error('Ошибка загрузки данных');
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await api.get('/settings/users/');
      setUsers(response.data);
    } catch (error) {
      toast.error('Ошибка загрузки пользователей');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const data = {
      ...formData,
      service_centers: formData.service_centers
        ? formData.service_centers.map(sc => sc.value).filter(id => id !== null)
        : [],
      roles: formData.roles
        ? formData.roles.map(role => role.value).filter(id => id !== null)
        : [],
      projects: formData.projects
        ? formData.projects.map(project => project.value).filter(id => id !== null)
        : [], // Добавлено
      guest_customers: formData.guest_customers
        ? formData.guest_customers.map(customer => customer.value).filter(id => id !== null)
        : [],
    };
    
    if (!data.password) {
      delete data.password;
    }

    try {
      if (editingId) {
        await api.put(`/settings/users/${editingId}/`, data);
        toast.success('Пользователь обновлён');
      } else {
        if (!data.password) {
          toast.error('Пароль обязателен для создания пользователя');
          return;
        }
        await api.post('/settings/users/', data);
        toast.success('Пользователь создан');
      }
      
      closeModal();
      fetchUsers();
    } catch (error) {
      console.error('Ошибка API:', error.response?.data, error.response?.status);
      toast.error(`Ошибка сохранения пользователя: ${JSON.stringify(error.response?.data)}`);
    }
  };

  const handleEdit = (user) => {
    const selectedServiceCenters = user.service_centers
      ? user.service_centers
          .map(scId => {
            const option = serviceCenters.find(opt => opt.value === scId);
            return option || null;
          })
          .filter(sc => sc !== null)
      : [];

    const selectedRoles = user.roles
      ? user.roles
          .map(roleId => {
            const option = roles.find(opt => opt.value === roleId);
            return option || null;
          })
          .filter(role => role !== null)
      : [];

    const selectedProjects = user.projects
      ? user.projects
          .map(projectId => {
            const option = projects.find(opt => opt.value === projectId);
            return option || null;
          })
          .filter(project => project !== null)
      : []; // Добавлено

    const selectedGuestCustomers = user.guest_customers
      ? user.guest_customers
          .map(customerId => {
            const option = customers.find(opt => opt.value === customerId);
            return option || null;
          })
          .filter(customer => customer !== null)
      : [];

    setFormData({
      username: user.username,
      first_name: user.first_name || '',
      last_name: user.last_name || '',
      email: user.email || '',
      telegram_chat_id: user.telegram_chat_id || '',
      password: '',
      service_centers: selectedServiceCenters,
      roles: selectedRoles,
      projects: selectedProjects, // Добавлено
      guest_customers: selectedGuestCustomers,
    });
    setEditingId(user.id);
    setIsModalOpen(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Удалить пользователя?')) {
      try {
        await api.delete(`/settings/users/${id}/`);
        toast.success('Пользователь удалён');
        fetchUsers();
      } catch (error) {
        toast.error('Ошибка удаления пользователя');
      }
    }
  };

  const openAddModal = () => {
    setFormData({
      username: '',
      first_name: '',
      last_name: '',
      email: '',
      telegram_chat_id: '',
      password: '',
      service_centers: [],
      roles: [],
      projects: [], // Добавлено
      guest_customers: [],
    });
    setEditingId(null);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setFormData({
      username: '',
      first_name: '',
      last_name: '',
      email: '',
      telegram_chat_id: '',
      password: '',
      service_centers: [],
      roles: [],
      projects: [], // Добавлено
      guest_customers: [],
    });
    setEditingId(null);
  };

  const getUserDisplayName = (user) => {
    if (user.first_name || user.last_name) {
      return `${user.first_name || ''} ${user.last_name || ''}`.trim();
    }
    return user.username;
  };

  const getRoleNames = (roleIds) => {
    if (!roleIds || roleIds.length === 0) return '—';
    return roleIds
      .map(roleId => {
        const found = roles.find(opt => opt.value === roleId);
        return found ? found.label : '—';
      })
      .join(', ');
  };

  const getServiceCenterNames = (scIds) => {
    if (!scIds || scIds.length === 0) return '—';
    return scIds
      .map(scId => {
        const found = serviceCenters.find(opt => opt.value === scId);
        return found ? found.label : '—';
      })
      .join(', ');
  };

  const getProjectNames = (projectIds) => {
    if (!projectIds || projectIds.length === 0) return '—';
    return projectIds
      .map(projectId => {
        const found = projects.find(opt => opt.value === projectId);
        return found ? found.label : '—';
      })
      .join(', ');
  }; // Добавлено

  const getCustomerNames = (customerIds) => {
    if (!customerIds || customerIds.length === 0) return '—';
    return customerIds
      .map(customerId => {
        const found = customers.find(opt => opt.value === customerId);
        return found ? found.label : '—';
      })
      .join(', ');
  };

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.loadingContent}>
          <div className={styles.spinner}></div>
          <p className={styles.loadingText}>Загрузка пользователей...</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className={styles.sectionHeader}>
        <h3>
          <User size={24} style={{ marginRight: '0.5rem', color: '#0f766e' }} />
          Пользователи системы
        </h3>
        <button className={styles.addButton} onClick={openAddModal}>
          <Plus size={18} />
          Добавить пользователя
        </button>
      </div>

      <div className={styles.tableWrapper}>
        {users.length === 0 ? (
          <div className={styles.emptyState}>
            <div className={styles.emptyIcon}>
              <User size={48} />
            </div>
            <h3>Нет пользователей</h3>
            <p>Добавьте первого пользователя для начала работы</p>
          </div>
        ) : (
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Пользователь</th>
                <th>Email</th>
                <th>Telegram</th>
                <th>Сервисные центры</th>
                <th>Роли</th>
                <th>Проекты</th> {/* Добавлено */}
                <th>Заказчики гостя</th>
                <th>Действия</th>
              </tr>
            </thead>
            <tbody>
              {users.map(user => (
                <tr key={user.id} className={styles.clickableRow} onClick={() => !loading && handleEdit(user)}>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                      <div style={{
                        width: '40px',
                        height: '40px',
                        borderRadius: '50%',
                        background: 'linear-gradient(135deg, #0f766e 0%, #14b8a6 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        fontWeight: '600',
                        fontSize: '0.875rem'
                      }}>
                        {getUserDisplayName(user).charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <div style={{ fontWeight: '600', color: '#1e293b' }}>
                          {getUserDisplayName(user)}
                        </div>
                        <div style={{ fontSize: '0.8125rem', color: '#64748b' }}>
                          @{user.username}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td>
                    {user.email ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <Mail size={14} style={{ color: '#64748b' }} />
                        {user.email}
                      </div>
                    ) : '—'}
                  </td>
                  <td>
                    {user.telegram_chat_id ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <Phone size={14} style={{ color: '#64748b' }} />
                        {user.telegram_chat_id}
                      </div>
                    ) : '—'}
                  </td>
                  <td>
                    <div style={{ maxWidth: '200px', fontSize: '0.8125rem' }}>
                      {getServiceCenterNames(user.service_centers)}
                    </div>
                  </td>
                  <td>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.25rem' }}>
                      {user.roles && user.roles.length > 0 ? (
                        user.roles.map(roleId => {
                          const role = roles.find(r => r.value === roleId);
                          return role ? (
                            <span
                              key={roleId}
                              style={{
                                background: 'linear-gradient(135deg, #ecfdf5, #d1fae5)',
                                color: '#065f46',
                                padding: '0.125rem 0.5rem',
                                borderRadius: '12px',
                                fontSize: '0.75rem',
                                fontWeight: '500'
                              }}
                            >
                              {role.label}
                            </span>
                          ) : null;
                        })
                      ) : (
                        <span style={{ color: '#94a3b8', fontSize: '0.8125rem' }}>—</span>
                      )}
                    </div>
                  </td>
                  <td>
                    <div style={{ maxWidth: '200px', fontSize: '0.8125rem' }}>
                      {getProjectNames(user.projects)} {/* Добавлено */}
                    </div>
                  </td>
                  <td>
                    <div style={{ maxWidth: '220px', fontSize: '0.8125rem' }}>
                      {getCustomerNames(user.guest_customers)}
                    </div>
                  </td>
                  <td>
                    <button
                      className={styles.actionButton}
                      onClick={(event) => { event.stopPropagation(); handleDelete(user.id); }}
                      title="Удалить"
                    >
                      <Trash2 size={16} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Modal */}
      {isModalOpen && (
        <div className={styles.modalOverlay} onClick={closeModal}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3 className={styles.modalTitle}>
                <User size={20} />
                {editingId ? 'Редактировать пользователя' : 'Добавить пользователя'}
              </h3>
              <button className={styles.modalCloseButton} onClick={closeModal}>
                <X size={20} />
              </button>
            </div>
            <div className={styles.modalBody}>
              <form onSubmit={handleSubmit} className={styles.form}>
                <div className={styles.formGroup}>
                  <label>
                    <User size={16} />
                    Имя пользователя
                  </label>
                  <input
                    type="text"
                    value={formData.username}
                    onChange={e => setFormData({ ...formData, username: e.target.value })}
                    placeholder="Введите имя пользователя"
                    required
                  />
                </div>

                <div className={styles.formGroup}>
                  <label>
                    <User size={16} />
                    Имя
                  </label>
                  <input
                    type="text"
                    value={formData.first_name}
                    onChange={e => setFormData({ ...formData, first_name: e.target.value })}
                    placeholder="Введите имя"
                  />
                </div>

                <div className={styles.formGroup}>
                  <label>
                    <User size={16} />
                    Фамилия
                  </label>
                  <input
                    type="text"
                    value={formData.last_name}
                    onChange={e => setFormData({ ...formData, last_name: e.target.value })}
                    placeholder="Введите фамилию"
                  />
                </div>

                <div className={styles.formGroup}>
                  <label>
                    <Mail size={16} />
                    Email
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={e => setFormData({ ...formData, email: e.target.value })}
                    placeholder="Введите email"
                  />
                </div>

                <div className={styles.formGroup}>
                  <label>
                    <Shield size={16} />
                    Пароль
                  </label>
                  <input
                    type="password"
                    value={formData.password}
                    onChange={e => setFormData({ ...formData, password: e.target.value })}
                    placeholder={editingId ? 'Оставьте пустым, чтобы не менять' : 'Введите пароль'}
                    required={!editingId}
                  />
                </div>

                <div className={styles.formGroup}>
                  <label>
                    <Phone size={16} />
                    Telegram Chat ID
                  </label>
                  <input
                    type="text"
                    value={formData.telegram_chat_id}
                    onChange={e => setFormData({ ...formData, telegram_chat_id: e.target.value })}
                    placeholder="Введите Telegram Chat ID"
                  />
                </div>

                <div className={styles.formGroup}>
                  <label>
                    <Wrench size={16} />
                    Сервисные центры
                  </label>
                  <div className={styles.select}>
                    <Select
                      isMulti
                      options={serviceCenters}
                      value={formData.service_centers}
                      onChange={selected => setFormData({ ...formData, service_centers: selected || [] })}
                      placeholder={serviceCenters.length ? 'Выберите сервисные центры...' : 'Загрузка...'}
                      isDisabled={!serviceCenters.length}
                    />
                  </div>
                </div>

                <div className={styles.formGroup}>
                  <label>
                    <Shield size={16} />
                    Роли
                  </label>
                  <div className={styles.select}>
                    <Select
                      isMulti
                      options={roles}
                      value={formData.roles}
                      onChange={selected => setFormData({ ...formData, roles: selected || [] })}
                      placeholder={roles.length ? 'Выберите роли...' : 'Загрузка...'}
                      isDisabled={!roles.length}
                    />
                  </div>
                </div>

                <div className={styles.formGroup}>
                  <label>
                    <Folder size={16} />
                    Проекты
                  </label>
                  <div className={styles.select}>
                    <Select
                      isMulti
                      options={projects}
                      value={formData.projects}
                      onChange={selected => setFormData({ ...formData, projects: selected || [] })}
                      placeholder={projects.length ? 'Выберите проекты...' : 'Загрузка...'}
                      isDisabled={!projects.length}
                    />
                  </div>
                </div>

                <div className={styles.formGroup}>
                  <label>
                    <Building2 size={16} />
                    Заказчики гостя
                  </label>
                  <div className={styles.select}>
                    <Select
                      isMulti
                      options={customers}
                      value={formData.guest_customers}
                      onChange={selected => setFormData({ ...formData, guest_customers: selected || [] })}
                      placeholder={customers.length ? 'Выберите заказчиков...' : 'Загрузка...'}
                      isDisabled={!customers.length}
                    />
                  </div>
                </div>

                <button type="submit" className={styles.submitButton}>
                  {editingId ? 'Обновить пользователя' : 'Создать пользователя'}
                </button>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserList;
